import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTour, TOUR_IDS } from '../contexts/TourContext';
import { 
  getDashboardSteps, 
  getStockDetailSteps, 
  CLICK_STOCK_STEP_INDEX,
  STOCK_DETAIL_START_INDEX,
  isInteractionStep,
  isRequiredActionStep,
  isModalStep,
  isModalSubflowStart,
  isModalSubflowEnd,
  getRequiredTarget,
  getWaitForSelector,
  getWaitForRemoved,
  getWaitTimeout
} from '../config/tourSteps';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, MousePointer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Cross-page Tour Implementation with Auto-scroll and Interactive Modal Subflows
 * 
 * This tour system supports:
 * 1. Continuing across page navigation (Dashboard -> Stock Detail)
 * 2. Auto-scrolling to elements below the fold
 * 3. Waiting for elements to exist before showing steps
 * 4. Allowing interaction on specific steps (e.g., AI Article Analysis button)
 * 5. Interactive modal subflows with required user actions (click-to-continue)
 * 
 * Key data attributes used:
 * - data-tour-stock-row="true" on clickable stock rows
 * - data-symbol="AAPL" on stock rows to identify which stock was clicked
 * - data-tour="sd-*" attributes on Stock Detail page elements
 * - data-tour="aa-*" attributes on Article Analysis modal elements
 * 
 * Modal Subflow State Machine:
 * - Step 7: User clicks "AI Article Analysis" button → Modal opens
 * - Step 8: User clicks "Fetch & Analyze" → Wait for results
 * - Step 9: User clicks X to close modal → Resume Stock Detail tour
 */

/**
 * Wait for a selector to appear in the DOM
 * @param {string} selector - CSS selector to wait for
 * @param {number} timeout - Max wait time in ms (default 5000)
 * @param {number} interval - Check interval in ms (default 150)
 * @returns {Promise<Element|null>} - The element or null if timeout
 */
const waitForSelector = (selector, timeout = 5000, interval = 150) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const check = () => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }
      
      if (Date.now() - startTime >= timeout) {
        console.log(`[Tour] waitForSelector timeout for: ${selector}`);
        resolve(null);
        return;
      }
      
      setTimeout(check, interval);
    };
    
    check();
  });
};

/**
 * Wait for a selector to be removed from the DOM
 * @param {string} selector - CSS selector to wait for removal
 * @param {number} timeout - Max wait time in ms (default 5000)
 * @param {number} interval - Check interval in ms (default 150)
 * @returns {Promise<boolean>} - true if removed, false if timeout
 */
const waitForRemoved = (selector, timeout = 5000, interval = 150) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const check = () => {
      const element = document.querySelector(selector);
      if (!element) {
        resolve(true);
        return;
      }
      
      if (Date.now() - startTime >= timeout) {
        console.log(`[Tour] waitForRemoved timeout for: ${selector}`);
        resolve(false);
        return;
      }
      
      setTimeout(check, interval);
    };
    
    check();
  });
};

/**
 * Smooth scroll element into view ensuring both element and tooltip are visible
 * @param {Element} element - Element to scroll to
 * @param {string} placement - Tooltip placement ('top', 'bottom', 'left', 'right')
 * @returns {Promise<void>}
 */
const scrollToElement = (element, placement = 'top') => {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }
    
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const tooltipHeight = 280; // Estimated tooltip height with padding
    const headerOffset = 80; // Account for fixed header
    const bottomPadding = 40; // Padding from bottom
    
    // Calculate the ideal scroll position based on tooltip placement
    let scrollTarget;
    
    if (placement === 'bottom' || placement === 'left' || placement === 'right') {
      // Tooltip appears below/beside - ensure element top is visible with space for tooltip below
      // Scroll so element is in upper third of viewport
      scrollTarget = window.scrollY + rect.top - headerOffset - 50;
    } else {
      // Tooltip appears above (default) - ensure there's space above the element for tooltip
      // Scroll so element is in lower half of viewport, with tooltip space above
      scrollTarget = window.scrollY + rect.top - tooltipHeight - headerOffset - 50;
    }
    
    // Ensure we don't scroll negative
    scrollTarget = Math.max(0, scrollTarget);
    
    // Check if the element would be below the fold after scroll
    const elementBottom = rect.bottom + (window.scrollY - scrollTarget);
    if (elementBottom > viewportHeight - bottomPadding) {
      // Adjust to ensure bottom of element is visible
      scrollTarget = window.scrollY + rect.bottom - viewportHeight + bottomPadding + 100;
    }
    
    window.scrollTo({
      top: scrollTarget,
      behavior: 'smooth'
    });
    
    // Wait for scroll to complete
    setTimeout(resolve, 500);
  });
};

// Custom tooltip component matching the dark theme
const TourTooltip = ({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onClose,
  position,
  isInteractionStep,
  isRequiredAction,
  requiredActionType,
}) => {
  const isLastStep = stepIndex === totalSteps - 1;
  const showInteractionHint = isInteractionStep && step.navigateTo;
  const showRequiredClickHint = isRequiredAction && requiredActionType === 'click';
  
  // Determine the appropriate hint text for required-action steps
  const getRequiredActionHint = () => {
    if (!showRequiredClickHint) return null;
    
    const target = step.requiredTarget;
    if (target?.includes('sd-ai-article-btn')) {
      return 'Click the button above to continue';
    } else if (target?.includes('aa-fetch-analyze')) {
      return "Click 'Fetch & Analyze' to continue";
    } else if (target?.includes('aa-close')) {
      return 'Click X to close and continue the tour';
    }
    return 'Click to continue';
  };
  
  return (
    <div
      className="fixed z-[10001] animate-in fade-in duration-200"
      style={{
        top: position.top,
        left: position.left,
        transform: position.transform || 'none',
      }}
    >
      {/* Arrow */}
      {position.arrow && (
        <div
          className="absolute w-3 h-3 rotate-45"
          style={{
            backgroundColor: '#0E1324',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            ...position.arrowStyle,
          }}
        />
      )}
      
      <div
        style={{
          backgroundColor: '#0E1324',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '360px',
          minWidth: '300px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px rgba(59, 130, 246, 0.15)',
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-[11px] text-slate-500 font-medium uppercase tracking-wider">
            Step {stepIndex + 1} of {totalSteps}
          </span>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/5 rounded transition-colors text-slate-500 hover:text-slate-300"
            aria-label="Close tutorial"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Title */}
        {step.title && (
          <h3 className="text-white text-base font-semibold mb-2">
            {step.title}
          </h3>
        )}

        {/* Content */}
        <p className="text-slate-400 text-sm leading-relaxed mb-4">
          {step.content}
        </p>

        {/* Interaction hint for click-to-continue steps */}
        {showInteractionHint && (
          <div 
            className="flex items-center gap-2 p-3 mb-4 rounded-lg"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
          >
            <MousePointer className="w-4 h-4 text-blue-400 animate-bounce" />
            <span className="text-blue-300 text-xs font-medium">
              Click any stock row to continue the tour
            </span>
          </div>
        )}

        {/* Required action hint for modal subflow steps */}
        {showRequiredClickHint && !showInteractionHint && (
          <div 
            className="flex items-center gap-2 p-3 mb-4 rounded-lg"
            style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)' }}
          >
            <MousePointer className="w-4 h-4 text-green-400 animate-bounce" />
            <span className="text-green-300 text-xs font-medium">
              {getRequiredActionHint()}
            </span>
          </div>
        )}

        {/* Progress bar */}
        <div className="h-[3px] bg-blue-500/20 rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={onSkip}
            className="text-slate-500 text-[13px] font-medium hover:text-slate-300 transition-colors px-2 py-1"
          >
            Skip tour
          </button>

          <div className="flex gap-2">
            {stepIndex > 0 && (
              <button
                onClick={onPrev}
                className="flex items-center gap-1 px-4 py-2 text-[13px] font-medium text-slate-400 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            {/* Hide Next button on interaction/required-action steps - user must click target */}
            {!showInteractionHint && !showRequiredClickHint && (
              <button
                onClick={onNext}
                className="flex items-center gap-1 px-5 py-2 text-[13px] font-semibold text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
              >
                {isLastStep ? 'Done' : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Spotlight overlay component with optional click-through
const SpotlightOverlay = ({ targetRect, allowInteraction, onOverlayClick }) => {
  if (!targetRect) return null;
  
  const padding = 8;
  const borderRadius = 8;
  
  return (
    <div 
      className="fixed inset-0 z-[9999]"
      style={{ 
        // Allow pointer events through the spotlight area when allowInteraction is true
        pointerEvents: allowInteraction ? 'none' : 'auto' 
      }}
      onClick={allowInteraction ? undefined : onOverlayClick}
    >
      <svg 
        className="w-full h-full" 
        style={{ pointerEvents: allowInteraction ? 'none' : 'auto' }}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - padding}
              y={targetRect.top - padding}
              width={targetRect.width + padding * 2}
              height={targetRect.height + padding * 2}
              rx={borderRadius}
              ry={borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: allowInteraction ? 'none' : 'auto' }}
        />
        {/* Highlight border - pulsing animation when interaction is allowed */}
        <rect
          x={targetRect.left - padding}
          y={targetRect.top - padding}
          width={targetRect.width + padding * 2}
          height={targetRect.height + padding * 2}
          rx={borderRadius}
          ry={borderRadius}
          fill="none"
          stroke={allowInteraction ? "rgba(59, 130, 246, 0.8)" : "rgba(59, 130, 246, 0.5)"}
          strokeWidth={allowInteraction ? "3" : "2"}
          className={allowInteraction ? "animate-pulse" : ""}
          style={{ pointerEvents: 'none' }}
        />
      </svg>
    </div>
  );
};

const AppTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    activeTour, 
    tourRunning, 
    stepIndex,
    setStepIndex,
    stopTour,
    markTourCompleted,
    pendingContinue,
    pendingTourId,
    pendingRoute,
    prepareForNavigation,
    cancelPendingTour,
    allowInteraction,
    setAllowInteraction,
    // Direct setters for tour resume
    setActiveTour,
    setTourRunning,
    setPendingContinue,
    setPendingTourId,
    setPendingRoute,
  } = useTour();
  
  const [steps, setSteps] = useState([]);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const stockClickListenerRef = useRef(null);
  const resumeAttempts = useRef(0);
  const maxResumeAttempts = 20; // 20 retries x 150ms = 3 seconds

  // Get steps based on active tour
  useEffect(() => {
    if (!activeTour || !tourRunning) {
      setSteps([]);
      return;
    }

    let tourSteps = [];
    
    if (activeTour === TOUR_IDS.DASHBOARD) {
      tourSteps = getDashboardSteps(true);
    } else if (activeTour === TOUR_IDS.STOCK_DETAIL) {
      tourSteps = getStockDetailSteps();
    }

    // Filter steps to only those with existing targets
    const validSteps = tourSteps.filter(step => {
      if (step.placement === 'center') return true;
      if (step.allowInteraction) return true; // Keep interaction steps even if target not perfect
      const element = document.querySelector(step.target);
      return element !== null;
    });

    setSteps(validSteps.length > 0 ? validSteps : []);
  }, [activeTour, tourRunning, location.pathname]);

  // Check if current step allows interaction
  useEffect(() => {
    if (steps.length > 0 && stepIndex < steps.length) {
      const currentStep = steps[stepIndex];
      setAllowInteraction(currentStep.allowInteraction === true);
    } else {
      setAllowInteraction(false);
    }
  }, [steps, stepIndex, setAllowInteraction]);

  // Setup click listener for stock rows when on interaction step
  useEffect(() => {
    if (!tourRunning || !allowInteraction || steps.length === 0) {
      return;
    }

    const currentStep = steps[stepIndex];
    if (!currentStep?.navigateTo) {
      return;
    }

    // Click handler for stock rows
    const handleStockClick = (event) => {
      // Find the closest stock row element
      const stockRow = event.target.closest('[data-tour-stock-row="true"]');
      if (!stockRow) return;

      const symbol = stockRow.getAttribute('data-symbol');
      if (!symbol) return;

      console.log('[Tour] Stock clicked:', symbol);

      // Prepare for navigation - store state for resuming on Stock Detail page
      prepareForNavigation(TOUR_IDS.STOCK_DETAIL, STOCK_DETAIL_START_INDEX, '/app/stock/');

      // Let the click propagate - the Dashboard will handle navigation
      // The tour will resume on Stock Detail page mount
    };

    // Use capturing phase to detect clicks before they're handled
    document.addEventListener('click', handleStockClick, true);
    stockClickListenerRef.current = handleStockClick;

    return () => {
      if (stockClickListenerRef.current) {
        document.removeEventListener('click', stockClickListenerRef.current, true);
        stockClickListenerRef.current = null;
      }
    };
  }, [tourRunning, allowInteraction, steps, stepIndex, prepareForNavigation]);

  // Required-action click listener for modal subflow steps
  // This handles steps that require user to click a specific element to continue
  const requiredActionListenerRef = useRef(null);
  const pendingWaitRef = useRef(false);  // Track if we're waiting for async operation
  
  useEffect(() => {
    if (!tourRunning || steps.length === 0) {
      return;
    }

    const currentStep = steps[stepIndex];
    
    // Only add listener for required-action steps
    if (!currentStep?.requiredAction || currentStep.requiredAction !== 'click') {
      return;
    }

    const requiredTarget = currentStep.requiredTarget;
    if (!requiredTarget) {
      return;
    }

    console.log('[Tour] Setting up required-action listener for:', requiredTarget);

    /**
     * Handle required action click
     * 1. Check if user clicked the required target
     * 2. Wait for next element (if waitForSelector specified)
     * 3. Or wait for element removal (if waitForRemoved specified)
     * 4. Then advance to next step
     */
    const handleRequiredClick = async (event) => {
      // Prevent multiple clicks while waiting
      if (pendingWaitRef.current) {
        console.log('[Tour] Already waiting for async operation, ignoring click');
        return;
      }
      
      // Check if user clicked the required target
      const clickedTarget = event.target.closest(requiredTarget);
      if (!clickedTarget) return;

      console.log('[Tour] Required target clicked:', requiredTarget);

      // Determine what to wait for
      const waitSelector = currentStep.waitForSelector;
      const waitRemoveSelector = currentStep.waitForRemoved;
      const timeout = currentStep.waitTimeout || 5000;

      // Mark that we're waiting
      pendingWaitRef.current = true;

      try {
        // If we need to wait for a new element to appear
        if (waitSelector) {
          console.log('[Tour] Waiting for selector:', waitSelector, 'timeout:', timeout);
          
          // Small delay to let the click action take effect (e.g., modal open, fetch start)
          await new Promise(resolve => setTimeout(resolve, 300));
          
          const element = await waitForSelector(waitSelector, timeout, 200);
          
          if (!element) {
            console.log('[Tour] Wait timeout - advancing anyway');
            toast.info("Tutorial couldn't detect the next UI state. Continuing...");
          } else {
            console.log('[Tour] Element found:', waitSelector);
          }
          
          // Advance to next step
          setStepIndex(prevIndex => prevIndex + 1);
        }
        // If we need to wait for an element to be removed (e.g., modal close)
        else if (waitRemoveSelector) {
          console.log('[Tour] Waiting for removal of:', waitRemoveSelector);
          
          // Small delay for the close action to take effect
          await new Promise(resolve => setTimeout(resolve, 300));
          
          const removed = await waitForRemoved(waitRemoveSelector, timeout, 200);
          
          if (!removed) {
            console.log('[Tour] Removal timeout - advancing anyway');
            toast.info("Tutorial couldn't detect modal close. Continuing...");
          } else {
            console.log('[Tour] Element removed:', waitRemoveSelector);
          }
          
          // Advance to next step
          setStepIndex(prevIndex => prevIndex + 1);
        }
        // No waiting required - advance immediately
        else {
          setStepIndex(prevIndex => prevIndex + 1);
        }
      } finally {
        pendingWaitRef.current = false;
      }
    };

    // Use capturing phase to detect clicks
    document.addEventListener('click', handleRequiredClick, true);
    requiredActionListenerRef.current = handleRequiredClick;

    return () => {
      if (requiredActionListenerRef.current) {
        document.removeEventListener('click', requiredActionListenerRef.current, true);
        requiredActionListenerRef.current = null;
      }
    };
  }, [tourRunning, steps, stepIndex, setStepIndex]);

  // Resume tour after navigation to Stock Detail page
  // This effect handles both React state and localStorage (for page refresh scenarios)
  useEffect(() => {
    // First check localStorage directly (in case React state hasn't synced yet)
    const pendingData = localStorage.getItem('stm_pending_tour');
    let shouldResume = pendingContinue && pendingTourId && pendingRoute;
    let resumeTourId = pendingTourId;
    let resumeRoute = pendingRoute;
    let resumeStepIndex = 0;
    
    // Always try to read from localStorage for stepIndex (more reliable)
    if (pendingData) {
      try {
        const parsed = JSON.parse(pendingData);
        // If context state not set, use localStorage completely
        if (!shouldResume) {
          shouldResume = true;
          resumeTourId = parsed.tourId;
          resumeRoute = parsed.route;
        }
        // Always use stepIndex from localStorage if present
        resumeStepIndex = parsed.stepIndex || 0;
        console.log('[Tour Resume] Found pending tour in localStorage:', parsed);
      } catch (e) {
        console.log('[Tour Resume] Error parsing localStorage:', e);
      }
    }
    
    console.log('[Tour Resume] Check - shouldResume:', shouldResume, 'pathname:', location.pathname, 'resumeRoute:', resumeRoute, 'stepIndex:', resumeStepIndex);
    
    if (!shouldResume || !resumeRoute) {
      return;
    }

    // Check if we're on the expected route
    if (!location.pathname.includes(resumeRoute)) {
      console.log('[Tour Resume] Route mismatch:', location.pathname, 'expected:', resumeRoute);
      return;
    }

    console.log('[Tour Resume] Route matched! Starting element check...');

    // We're on Stock Detail page - wait for elements to be ready
    const checkAndResume = () => {
      resumeAttempts.current += 1;
      console.log('[Tour Resume] Attempt', resumeAttempts.current);

      // Check if key Stock Detail elements exist (using new data-tour attributes)
      const anomalyMove = document.querySelector('[data-tour="sd-anomaly-move"]');
      const delayedPrice = document.querySelector('[data-tour="sd-delayed-price"]');
      const chart = document.querySelector('[data-tour="sd-daily-chart"]');
      const headerFallback = document.querySelector('[data-testid="signal-summary-bar"]');
      
      console.log('[Tour Resume] Elements:', { 
        anomalyMove: !!anomalyMove, 
        delayedPrice: !!delayedPrice, 
        chart: !!chart,
        headerFallback: !!headerFallback 
      });
      
      if (anomalyMove || delayedPrice || chart || headerFallback) {
        // Elements ready - resume tour
        console.log('[Tour Resume] Stock Detail elements ready, resuming tour with tourId:', resumeTourId, 'stepIndex:', resumeStepIndex);
        resumeAttempts.current = 0;
        
        // Clear localStorage first
        localStorage.removeItem('stm_pending_tour');
        
        // Start the stock detail tour
        setActiveTour(resumeTourId);
        setStepIndex(resumeStepIndex);
        setTourRunning(true);
        setAllowInteraction(false);
        
        // Also clear context state
        setPendingContinue(false);
        setPendingTourId(null);
        setPendingRoute(null);
        
        return;
      }

      if (resumeAttempts.current >= maxResumeAttempts) {
        // Max retries reached - show message and cancel
        console.log('[Tour Resume] Max retries reached, canceling tour');
        toast.info("Tutorial couldn't find elements on this page. You can restart from Settings.");
        localStorage.removeItem('stm_pending_tour');
        cancelPendingTour();
        resumeAttempts.current = 0;
        return;
      }

      // Retry after 150ms
      setTimeout(checkAndResume, 150);
    };

    // Start checking after a small delay to let the page render
    const timer = setTimeout(checkAndResume, 500);
    return () => clearTimeout(timer);
  }, [pendingContinue, pendingTourId, pendingRoute, location.pathname, cancelPendingTour, setActiveTour, setStepIndex, setTourRunning, setAllowInteraction, setPendingContinue, setPendingTourId, setPendingRoute]);

  // Calculate target position and tooltip placement
  const updatePositions = useCallback(() => {
    if (!steps.length || stepIndex >= steps.length) return;
    
    const currentStep = steps[stepIndex];
    
    if (currentStep.placement === 'center') {
      setTargetRect(null);
      setTooltipPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }
    
    const target = document.querySelector(currentStep.target);
    if (!target) {
      return;
    }
    
    const rect = target.getBoundingClientRect();
    setTargetRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
    
    // Calculate tooltip position
    const placement = currentStep.placement || 'bottom';
    const tooltipWidth = 360;
    const tooltipHeight = 280;
    const padding = 16;
    
    let top, left;
    let arrow = true;
    let arrowStyle = {};
    
    switch (placement) {
      case 'top':
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowStyle = { bottom: -6, left: '50%', marginLeft: -6, borderTop: 'none', borderLeft: 'none' };
        break;
      case 'bottom':
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowStyle = { top: -6, left: '50%', marginLeft: -6, borderBottom: 'none', borderRight: 'none' };
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        arrowStyle = { right: -6, top: '50%', marginTop: -6, borderTop: 'none', borderRight: 'none' };
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding;
        arrowStyle = { left: -6, top: '50%', marginTop: -6, borderBottom: 'none', borderLeft: 'none' };
        break;
      default:
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowStyle = { top: -6, left: '50%', marginLeft: -6, borderBottom: 'none', borderRight: 'none' };
    }
    
    // Boundary checks - ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const headerOffset = 80; // Account for fixed header
    
    // Horizontal boundary checks
    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) left = viewportWidth - tooltipWidth - padding;
    
    // Vertical boundary checks - if tooltip would go off screen, flip it
    if (top < headerOffset) {
      // Tooltip would be above viewport or under header - flip to bottom
      top = rect.bottom + padding;
      arrowStyle = { top: -6, left: '50%', marginLeft: -6, borderBottom: 'none', borderRight: 'none' };
      
      // If still off screen after flipping, position at a safe spot
      if (top + tooltipHeight > viewportHeight - padding) {
        // Both above and below are bad - position in visible area
        top = Math.max(headerOffset + padding, viewportHeight / 2 - tooltipHeight / 2);
        arrow = false; // Hide arrow since we can't point accurately
      }
    }
    if (top + tooltipHeight > viewportHeight - padding) {
      // Tooltip would be below viewport - flip to top
      top = rect.top - tooltipHeight - padding;
      arrowStyle = { bottom: -6, left: '50%', marginLeft: -6, borderTop: 'none', borderLeft: 'none' };
      
      // If still off screen after flipping, position in visible area
      if (top < headerOffset) {
        top = Math.max(headerOffset + padding, viewportHeight / 2 - tooltipHeight / 2);
        arrow = false; // Hide arrow since we can't point accurately
      }
    }
    
    setTooltipPosition({ top, left, transform: 'none', arrow, arrowStyle });
  }, [steps, stepIndex]);

  // Update positions on step change and resize
  // Includes auto-scroll for below-fold elements
  useEffect(() => {
    if (!tourRunning || !steps.length) return;
    
    let mounted = true;
    
    const handleStepChange = async () => {
      const currentStep = steps[stepIndex];
      if (!currentStep) return;
      
      // For center placement, no element needed
      if (currentStep.placement === 'center') {
        updatePositions();
        return;
      }
      
      // Wait for the target element to exist (handles slow loads)
      const targetSelector = currentStep.target;
      const element = await waitForSelector(targetSelector, 3000, 150);
      
      if (!mounted) return;
      
      if (!element) {
        console.log(`[Tour] Step ${stepIndex}: Target not found, skipping:`, targetSelector);
        // Skip this step if target not found
        if (stepIndex < steps.length - 1) {
          setStepIndex(stepIndex + 1);
        }
        return;
      }
      
      // Get element's current position
      const rect = element.getBoundingClientRect();
      const tooltipHeight = 280; // Estimated tooltip height
      const headerOffset = 80;
      const bottomPadding = 100;
      
      // Check if element AND tooltip will be visible
      // For top placement, tooltip appears above so we need space above
      // For bottom placement, tooltip appears below so we need space below
      const placement = currentStep.placement || 'top';
      let needsScroll = false;
      
      if (currentStep.requiresScroll) {
        needsScroll = true;
      } else if (placement === 'top' || placement === 'left') {
        // Tooltip above/left - need element visible with space above for tooltip
        const spaceAbove = rect.top - headerOffset;
        if (spaceAbove < tooltipHeight || rect.bottom > window.innerHeight - bottomPadding) {
          needsScroll = true;
        }
      } else {
        // Tooltip below/right - need element visible with space below for tooltip  
        const spaceBelow = window.innerHeight - rect.bottom;
        if (rect.top < headerOffset || spaceBelow < tooltipHeight) {
          needsScroll = true;
        }
      }
      
      if (needsScroll) {
        await scrollToElement(element, placement);
        if (!mounted) return;
      }
      
      // Update positions after scroll
      updatePositions();
    };
    
    handleStepChange();
    
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions);
    
    return () => {
      mounted = false;
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions);
    };
  }, [tourRunning, steps, stepIndex, updatePositions, setStepIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!tourRunning) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        stopTour();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        // Don't auto-advance on interaction steps
        if (!allowInteraction) {
          handleNext();
        }
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tourRunning, stepIndex, steps, allowInteraction]);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Tour complete
      if (activeTour) {
        markTourCompleted(activeTour);
      }
      stopTour();
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleSkip = () => {
    if (activeTour) {
      markTourCompleted(activeTour);
    }
    stopTour();
  };

  const handleClose = () => {
    stopTour();
  };

  if (!tourRunning || steps.length === 0) {
    return null;
  }

  const currentStep = steps[stepIndex];
  const isCurrentStepInteraction = currentStep?.allowInteraction === true;
  const isCurrentStepRequiredAction = currentStep?.requiredAction === 'click';

  return createPortal(
    <>
      <SpotlightOverlay 
        targetRect={targetRect} 
        allowInteraction={isCurrentStepInteraction}
        onOverlayClick={() => {}}
      />
      <TourTooltip
        step={currentStep}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onClose={handleClose}
        position={tooltipPosition}
        isInteractionStep={isCurrentStepInteraction}
        isRequiredAction={isCurrentStepRequiredAction}
        requiredActionType={currentStep?.requiredAction}
      />
    </>,
    document.body
  );
};

export default AppTour;
